import React, { Suspense, useState, useContext, useMemo } from 'react';
import {
  AppBar, Box, CssBaseline, Divider, Drawer, IconButton,
  List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  Toolbar, Tabs, Tab, Paper, BottomNavigation, BottomNavigationAction
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import SportsGymnasticsIcon from '@mui/icons-material/SportsGymnastics';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AssistantIcon from '@mui/icons-material/Assistant';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import PersonIcon from '@mui/icons-material/Person';
import { useTheme } from '@mui/material/styles';
import { signOut } from "firebase/auth";
import { auth } from '../firebase-config';
import { AppCTX } from '../Data/AppData';
import SimpleLoader from './SimpleLoader';

// Lazy load components only once
const ManageTraining = React.lazy(() => import('../pages/ManageTraining'));
const RecipieBook = React.lazy(() => import('../pages/RecipieBook'));
const AiAssistant = React.lazy(() => import('../pages/AiAssistant'));
const ProfilePage = React.lazy(() => import('../pages/ProfilePage'));

const drawerWidth = 240;

// Extracted ripple animation component for reuse
const RippleEffect = () => (
  <Box 
    sx={{ 
      position: 'absolute',
      width: 35,
      height: 35,
      borderRadius: '50%',
      bgcolor: 'rgba(33, 50, 57, 0.1)',
      animation: 'ripple 1.5s infinite ease-in-out',
      '@keyframes ripple': {
        '0%': {
          transform: 'scale(0.8)',
          opacity: 1
        },
        '100%': {
          transform: 'scale(1.5)',
          opacity: 0
        }
      }
    }}
  />
);

// Extracted navigation icon component for reuse
const NavIcon = ({ icon: Icon, isSelected }) => (
  <Box sx={{ 
    position: 'relative', 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center',
    height: 24
  }}>
    <Icon 
      sx={{ 
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: isSelected ? 'scale(1.2)' : 'scale(1)'
      }} 
    />
    {isSelected && <RippleEffect />}
  </Box>
);

function Navigation(props) {
  const { selectedDay, setSelectedDay, editingMode, setEditingMode } = useContext(AppCTX);
  const { children, page, setPage } = props;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [selectedPopUpPage, setSelectedPopUpPage] = useState(0);
  const theme = useTheme();
  
  // Memoize popup pages to prevent unnecessary re-renders
  const popUpPages = useMemo(() => [
    ['Manage Training', ManageTraining, <SportsGymnasticsIcon />],
    ['Recipe Book', RecipieBook, <MenuBookIcon />],
    ['AI Assistant', AiAssistant, <AssistantIcon />],
    ['Profile', ProfilePage, <PersonIcon />]
  ], []);

  // Memoize days array
  const days = useMemo(() => [
    "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"
  ], []);

  const handleDrawerClose = () => {
    setIsClosing(true);
    setMobileOpen(false);
  };

  const handleDrawerTransitionEnd = () => {
    setIsClosing(false);
  };

  const handleDrawerToggle = () => {
    if (!isClosing) {
      setMobileOpen(!mobileOpen);
    }
  };

  const handleOpenPage = (pageIndex) => {
    setEditingMode(true);
    setSelectedPopUpPage(pageIndex);
  };

  const handleSignOut = () => {
    signOut(auth).catch((error) => {
      console.error("Sign out error:", error);
    });
  };

  // Memoize drawer content to prevent re-renders
  const drawer = useMemo(() => (
    <div>
      <Toolbar />
      <Divider />
      <List>
        {popUpPages.map((page, index) => (
          <ListItem disablePadding key={index}>
            <ListItemButton onClick={() => handleOpenPage(index)}>
              <ListItemIcon>
                {page[2]}
              </ListItemIcon>
              <ListItemText primary={page[0]} />
            </ListItemButton>
          </ListItem>
        ))}
        <ListItem disablePadding>
          <ListItemButton onClick={handleSignOut}>
            <ListItemIcon>
              <LogoutIcon sx={{ color: 'white' }} />
            </ListItemIcon>
            <ListItemText primary={'Logout'} />
          </ListItemButton>
        </ListItem>
      </List>
    </div>
  ), [popUpPages]);

  // Memoize bottom navigation styles
  const bottomNavStyles = useMemo(() => ({
    bgcolor: 'transparent',
    height: 80,
    '& .MuiBottomNavigationAction-root': {
      color: 'rgba(255, 255, 255, 0.7)',
      position: 'relative',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      overflow: 'hidden',
      paddingTop: 1.5,
      '&.Mui-selected': {
        color: '#9FD218',
        fontWeight: 'bold'
      },
      '& .MuiBottomNavigationAction-label': {
        fontSize: '0.75rem',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        opacity: 0.7,
        marginTop: 0.5
      },
      '&.Mui-selected .MuiBottomNavigationAction-label': {
        fontSize: '0.875rem',
        opacity: 1
      }
    }
  }), []);

  // Memoize paper styles
  const paperStyles = useMemo(() => ({ 
    position: 'fixed', 
    bottom: -1, 
    left: 0, 
    right: 0,
    zIndex: 1000,
    borderRadius: '16px 16px 0 0',
    overflow: 'hidden',
    boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.15)',
    backdropFilter: 'blur(10px)',
    backgroundColor: '#213239',
    backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.09), rgba(255, 255, 255, 0.09))',
    border: '1px solid rgba(255, 255, 255, 0.18)',
    paddingBottom: { xs: '20px', sm: '10px' }
  }), []);

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
        <AppBar
          position="fixed"
          sx={{
            width: { sm: `calc(100% - ${drawerWidth}px)` },
            ml: { sm: `${drawerWidth}px` },
          }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { sm: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
            {page===2 ? null : 
              <Tabs
                value={selectedDay}
                onChange={(event, newValue) => setSelectedDay(newValue)}
                indicatorColor="secondary"
                textColor="inherit"
                variant="scrollable"
                aria-label="day selector tabs"
              >
                {days.map((day, index) => (
                  <Tab key={day} label={day} />
                ))}
              </Tabs>
            }
          </Toolbar>
        </AppBar>
    
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="navigation drawer"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onTransitionEnd={handleDrawerTransitionEnd}
          onClose={handleDrawerClose}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', bgcolor: '#192327', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { zIndex: '4', boxSizing: 'border-box', bgcolor: '#192327', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 0, pt:1, pb:5, width: { sm: `calc(100% - ${drawerWidth}px)` } }}
      >
        {children}
      </Box>
      <Paper sx={paperStyles} elevation={0}>
        <BottomNavigation
          value={page}
          onChange={(event, newValue) => setPage(newValue)}
          showLabels
          sx={bottomNavStyles}
        >
          <BottomNavigationAction 
            label="Training" 
            icon={<NavIcon icon={FitnessCenterIcon} isSelected={page === 0} />}
          />
          <BottomNavigationAction 
            label="Nutrition" 
            icon={<NavIcon icon={RestaurantIcon} isSelected={page === 1} />}
          />
          <BottomNavigationAction 
            label="Progress" 
            icon={<NavIcon icon={ShowChartIcon} isSelected={page === 2} />}
          />
        </BottomNavigation>
      </Paper>
      
      {editingMode && (
        <Suspense fallback={<SimpleLoader />}>
          {React.createElement(popUpPages[selectedPopUpPage][1])}
        </Suspense>
      )}
    </Box>
  );
}

export default React.memo(Navigation);








